/**
 * Générateur — Automatisme : compléter des multiplications à trou ; faire le
 * lien entre multiplication et division.
 * Attendu ID : 1095 | Niveau : 4ème | Domaine : Opérations sur les nombres relatifs
 * Rubrique : AUTOMATISME (programme 2025, cycle 4 page 9, ligne 636 :
 * « Compléter des multiplications à trou : 5 × … = 3 ; faire le lien entre
 * multiplication et division. »).
 *
 * ── Cadrage (sous-agent programme-maths, brouillon 2026-09-12) ─────────────
 * Deux morceaux. (a) « 5 × … = 3 » est le geste 1 de 1049 (5e l. 552 :
 * 3 × … = 7 → 7/3), entretien. (b) « faire le lien entre multiplication et
 * division » est LE NEUF, que 1049 et 1063 excluaient explicitement en le
 * renvoyant ici : le trou de 5 × … = 3 s'écrit aussi 3 ÷ 5 ; 56 ÷ 7 = 8
 * PARCE QUE 7 × 8 = 56 (1047 avait renvoyé « 56 ÷ 7 » à cet automatisme) ;
 * nommer le calcul qui donne le trou (3 ÷ 5, pas 5 ÷ 3), jumeau de 1046
 * côté addition (« je calcule 7 − 2 »). La mise en œuvre Eduscol 2026 : le
 * quotient de relatifs (objectif l. 642) « est introduit à partir d'exemples
 * de multiplications à trous » ; c'est ce que cet automatisme prépare, avec
 * des POSITIFS.
 * Frontières : quotient de relatifs (−12 ÷ 3 : objectif l. 642, c'est le
 * seul contenu que MathsMentales rattache ici, 5NC6, rejeté) ; le geste 2
 * « 7 × 3/7 = 3 » (Rationnels 4e l. 655, id 1099) ; l'écriture décimale
 * 3 ÷ 5 = 0,6 (1048) ; la lettre 5x = 3 (objectif) ; la division euclidienne
 * (1040). Pas un wrapper : 1049 mêle les deux gestes dans sa série flash.
 *
 * ── Plan de cartes ─────────────────────────────────────────────────────────
 *   Carte 1 « Série flash » (flash_texte_trou_saisie) : quatre lignes KaTeX
 *     dans un ordre fixe : « 5 × [ ] = 3 » (fraction à deux cases, gabarit
 *     622/1049, b non multiple de a) ; « 7 × … = 56 » (facteur de table
 *     manquant, entier) ; « 56 ÷ 7 = … » (la même chose lue en division) ;
 *     « 3 ÷ 5 = [ ] » (le quotient qui ne tombe pas juste s'écrit en
 *     fraction : le LIEN, dans le sens division → nombre). Correction
 *     positionnelle et exacte (strict), comme 1049.
 *   Carte 2 « Quel calcul ? » (calcul_qcm_unique) : « Quel calcul donne le
 *     nombre qui complète 5 × … = 3 ? », couple de table une fois sur deux ;
 *     QUATRE calculs : b ÷ a (clé), a ÷ b (inversion, la principale), b − a
 *     ou a − b (soustraction), a × b (produit) ; valeurs deux à deux
 *     distinctes par tirage (rejet sinon).
 *   Carte 3 « Multiplication ou division ? » (sens_relier) : TROIS
 *     multiplications à trou à relier à la division qui donne le trou, une
 *     de table (7 × … = 56 ↔ 56 ÷ 7), une qui ne tombe pas juste (5 × … = 3
 *     ↔ 3 ÷ 5) et une tirée, trou devant ou derrière ; CINQ divisions, les
 *     deux en trop sont les inversions de deux couples (arbitrage prof
 *     2026-09-12 : quatre pour cinq se résolvait par élimination).
 *
 * INVARIANTS : facteur connu a de 2 à 9 ; b de 1 à 20 non multiple de a
 * (1049) pour les trous en fraction ; faits de table a × k, a et k de 2 à 9,
 * pour les trous entiers ; tout en entiers, chaque égalité re-vérifiée.
 * SACRIFIÉS (fiches d'aide, importés) : (4 ; 13) de 1049 (« 4 × … = 13 »,
 * barre 13 en 4 parts) et 7 × 8 = 56 de 1047 (« 56 ÷ 7 = 8 » ira dans la
 * fiche) : aucun des deux ne sort d'aucune carte (auto-testé).
 */
import {
    randomChoice, randInt, shuffle,
    makeQcm, makeTexteTrou, makeRelier, tex,
} from '../../../utils';
import { COUPLES_SACRIFIES as SACRIFIES_1049 } from '../../5eme/nombres-rationnels/gen_1049';
import { COUPLE_SACRIFIE as SACRIFIE_1047 } from '../../5eme/nombres-rationnels/gen_1047';

const ATTENDU_ID = 1095;
const opts = (label) => ({ niveau: '4eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const L_FLASH = 'Série flash';
const L_QCM = 'Quel calcul ?';
const L_SENS = 'Multiplication ou division ?';

const FOIS = '×';
const DIVISE = '÷';
const A_MIN = 2;
const A_MAX = 9;
const B_MAX = 20;

// ─── Le domaine ───────────────────────────────────────────────────────────

/** Les couples des fiches d'aide, sacrifiés : (4 ; 13) de 1049, 7 × 8 = 56 de 1047 (dans les deux ordres). */
export const COUPLES_SACRIFIES = [
    ...SACRIFIES_1049.map(({ a, b }) => ({ a, b })),
    { a: SACRIFIE_1047.a, b: SACRIFIE_1047.a * SACRIFIE_1047.b },
    { a: SACRIFIE_1047.b, b: SACRIFIE_1047.a * SACRIFIE_1047.b },
];
const estSacrifie = (a, b) => COUPLES_SACRIFIES.some((s) => s.a === a && s.b === b);

/** Un couple (a, b) : a × … = b. `table` impose b = a × k (k de 2 à 9), sinon b non multiple de a (assert). */
const verifierCouple = (a, b, table) => {
    if (!Number.isInteger(a) || !Number.isInteger(b) || a < A_MIN || a > A_MAX || b < 1 || a === b) {
        throw new Error(`gen_1095 : couple hors domaine ${a} x ... = ${b}`);
    }
    if (table) {
        const k = b / a;
        if (!Number.isInteger(k) || k < A_MIN || k > A_MAX) throw new Error(`gen_1095 : pas un fait de table ${a} x ... = ${b}`);
    } else if (b > B_MAX || b % a === 0) throw new Error(`gen_1095 : multiple ou hors borne ${a} x ... = ${b}`);
    if (estSacrifie(a, b)) throw new Error('gen_1095 : couple sacrifié tiré');
    return { a, b, table };
};

/** a × … = b avec b non multiple de a : le trou est la fraction b/a. */
const tirerFraction = () => {
    for (let essai = 0; essai < 80; essai++) {
        const a = randInt(A_MIN, A_MAX);
        const b = randInt(1, B_MAX);
        if (a === b || b % a === 0 || estSacrifie(a, b)) continue;
        return verifierCouple(a, b, false);
    }
    return verifierCouple(3, 7, false);
};

/** a × … = b avec b = a × k : le trou est l'entier k. */
const tirerTable = () => {
    for (let essai = 0; essai < 80; essai++) {
        const a = randInt(A_MIN, A_MAX);
        const k = randInt(A_MIN, A_MAX);
        if (a === k || estSacrifie(a, a * k)) continue;
        return verifierCouple(a, a * k, true);
    }
    return verifierCouple(6, 54, true);
};

const cle = (c) => `${c.a}|${c.b}`;

// ─── Écritures ────────────────────────────────────────────────────────────

/** Fraction à deux cases (gabarit 622/1049, étais \rule pour garder la barre visible) : numérateur b, dénominateur a. */
const fracDeuxCases = (c) => `\\dfrac{{{${c.b}}}\\rule[-0.55em]{0pt}{0.55em}}{\\rule{0pt}{1.3em}{{${c.a}}}}`;

/** « 7 × … = 56 » ou « … × 7 = 56 » en texte brut (relier). */
const multiplicationTrou = (c, trouDevant) => (trouDevant ? `… ${FOIS} ${c.a} = ${c.b}` : `${c.a} ${FOIS} … = ${c.b}`);

const EXPLICATIONS = {
    inversion: 'Le nombre cherché, c\'est le résultat divisé par le facteur : 3 ÷ 5, pas 5 ÷ 3.',
    soustraction: 'Le trou n\'est pas ce qui manque pour aller de 5 à 3 : c\'est ce par quoi on multiplie 5.',
    produit: 'Multiplier 5 par 3 ne donne pas le nombre qui, multiplié par 5, donne 3.',
    regle: 'Compléter 5 × … = 3, c\'est calculer 3 ÷ 5 : le nombre qui, multiplié par 5, donne 3. Quand ça tombe juste, c\'est un fait de table (56 ÷ 7 = 8 parce que 7 × 8 = 56) ; sinon, c\'est la fraction 3 sur 5.',
};

// ─── Codes d'erreur (contenu.erreurs, codes communs du catalogue) ─────────

/** Le code commun d'un code du gen (facteur_voisin et saut_de_deux de 1047 compris) ; inversion a son propre code depuis le lot 2 (04/10/2026, docs/SFD_CODES_ERREUR.md). */
const CODE_COMMUN = {
    inversion: 'quotient_a_l_envers', soustraction: 'trou_par_soustraction', produit: 'produit_des_nombres_vus',
    facteur_voisin: 'fait_de_table_voisin', saut_de_deux: 'fait_de_table_voisin',
};

/** Les erreurs d'un trou entier k = b ÷ a : seul le fait voisin se nomme sur « b ÷ a », la soustraction et le produit y restent des concurrents sans nom. */
const erreursEntier = ({ a, b }, trou, division) => {
    const k = b / a;
    const cands = [[b - a, 'soustraction'], [a * b, 'produit'], ...[k - 1, k + 1, k - 2, k + 2].filter((v) => v >= A_MIN && v <= A_MAX).map((v) => [v, 'facteur_voisin'])]
        .map(([v, code]) => [v, division && code !== 'facteur_voisin' ? null : CODE_COMMUN[code]]);
    return cands
        .filter(([v, code], i) => code && v !== k && cands.findIndex(([w]) => w === v) === i && cands.every(([w, c]) => w !== v || c === code))
        .map(([v, code]) => ({ code, trou, valeur: String(v) }));
};

const pgcd = (x, y) => (y === 0 ? x : pgcd(y, x % y));

/** La fraction à deux cases b/a écrite à l'envers, a/b : chaque case porte l'inversion, sauf si une écriture juste (réduite ou multipliée) y met la même valeur. */
const erreursFraction = ({ a, b }, trou) => {
    const g = pgcd(a, b);
    const justes = [[b / g, a / g], ...[2, 3, 4, 5, 6].map((m) => [m * b, m * a])];
    return [
        ...(justes.some(([n]) => n === a) ? [] : [{ code: CODE_COMMUN.inversion, trou, valeur: String(a) }]),
        ...(justes.some(([, d]) => d === b) ? [] : [{ code: CODE_COMMUN.inversion, trou: trou + 1, valeur: String(b) }]),
    ];
};

// ─── Carte 1 : Série flash (4 lignes saisie) ──────────────────────────────

const CONSIGNE_FLASH = `<p><b>Complète chaque égalité.</b> Rappel : ${tex('\\dfrac{a}{b}')} est le nombre qui, multiplié par ${tex('b')}, donne ${tex('a')} ; c'est aussi ${tex('a \\div b')}.</p>`;

const lignesFlash = (f1, t2, t3, f4) => [
    // geste 1 : la fraction à deux cases, facteur devant ou derrière
    randomChoice([true, false]) ? `$${f1.a} \\times ${fracDeuxCases(f1)} = ${f1.b}$` : `$${fracDeuxCases(f1)} \\times ${f1.a} = ${f1.b}$`,
    // facteur de table manquant, entier, trou devant ou derrière
    randomChoice([true, false]) ? `$${t2.a} \\times {{${t2.b / t2.a}}} = ${t2.b}$` : `${'$'}{{${t2.b / t2.a}}} \\times ${t2.a} = ${t2.b}$`,
    // la même chose lue en division
    `$${t3.b} \\div ${t3.a} = {{${t3.b / t3.a}}}$`,
    // le lien : un quotient qui ne tombe pas juste s'écrit en fraction
    `$${f4.b} \\div ${f4.a} = ${fracDeuxCases(f4)}$`,
];

const assemblerFlash = (items) => {
    const phrase = lignesFlash(...items).join('\n');
    const payload = makeTexteTrou(phrase, CONSIGNE_FLASH, { ...opts(L_FLASH), mode: 'saisie', strict: true });
    payload.contenu = { ...payload.contenu, explications: { regle: EXPLICATIONS.regle } };
    payload._meta.params = { items };
    // trous : 0-1 (fraction), 2 (facteur de table), 3 (quotient de table), 4-5 (fraction)
    const [f1, t2, t3, f4] = items;
    payload.contenu = { ...payload.contenu, erreurs: [...erreursFraction(f1, 0), ...erreursEntier(t2, 2, false), ...erreursEntier(t3, 3, true), ...erreursFraction(f4, 4)] };
    return payload;
};

const genFlash = () => {
    for (let essai = 0; essai < 80; essai++) {
        const items = [tirerFraction(), tirerTable(), tirerTable(), tirerFraction()];
        if (new Set(items.map(cle)).size < 4) continue;
        return assemblerFlash(items);
    }
    return assemblerFlash([verifierCouple(3, 7, false), verifierCouple(6, 54, true), verifierCouple(4, 36, true), verifierCouple(5, 8, false)]);
};

// ─── Carte 2 : QCM « Quel calcul ? » ──────────────────────────────────────

/** Valeur exacte d'un calcul { num, den } ; égalité en croix. */
const memeValeur = (x, y) => x.num * y.den === y.num * x.den;

const genQcm = () => {
    for (let essai = 0; essai < 80; essai++) {
        const c = randomChoice([true, false]) ? tirerTable() : tirerFraction();
        const { a, b } = c;
        const bon = { texte: `$${b} \\div ${a}$`, num: b, den: a };
        const faux = [
            { texte: `$${a} \\div ${b}$`, code: 'inversion', num: a, den: b },
            b > a ? { texte: `$${b} - ${a}$`, code: 'soustraction', num: b - a, den: 1 } : { texte: `$${a} - ${b}$`, code: 'soustraction', num: a - b, den: 1 },
            { texte: `$${a} \\times ${b}$`, code: 'produit', num: a * b, den: 1 },
        ];
        const tous = [bon, ...faux];
        // quatre valeurs deux à deux distinctes : un calcul égal à un autre en valeur ne serait pas une erreur tranchée
        if (tous.some((x, i) => tous.some((y, j) => j > i && memeValeur(x, y)))) continue;
        const trouDevant = randomChoice([true, false]);
        const egalite = trouDevant ? `\\ldots \\times ${a} = ${b}` : `${a} \\times \\ldots = ${b}`;
        const payload = makeQcm(
            tous.map((x) => x.texte),
            bon.texte,
            `<p><b>Quel calcul donne le nombre qui complète ${tex(egalite)} ?</b></p>`,
            opts(L_QCM),
            false,
        );
        payload.contenu = { ...payload.contenu, explications: EXPLICATIONS };
        payload._meta.params = { ...c, trouDevant, distracteurs: faux.map(({ code }) => ({ code })) };
        payload.contenu = { ...payload.contenu, erreurs: faux.map((x) => ({ code: CODE_COMMUN[x.code], valeur: x.texte })) };
        return payload;
    }
    const c = verifierCouple(5, 3, false);
    const payload = makeQcm(['$3 \\div 5$', '$5 \\div 3$', '$5 - 3$', '$5 \\times 3$'], '$3 \\div 5$', `<p><b>Quel calcul donne le nombre qui complète ${tex('5 \\times \\ldots = 3')} ?</b></p>`, opts(L_QCM), false);
    payload.contenu = { ...payload.contenu, explications: EXPLICATIONS };
    payload._meta.params = { ...c, trouDevant: false, distracteurs: [{ code: 'inversion' }, { code: 'soustraction' }, { code: 'produit' }] };
    payload.contenu = { ...payload.contenu, erreurs: [['$5 \\div 3$', 'inversion'], ['$5 - 3$', 'soustraction'], ['$5 \\times 3$', 'produit']].map(([valeur, code]) => ({ code: CODE_COMMUN[code], valeur })) };
    return payload;
};

// ─── Carte 3 : Relier (4 multiplications à trou, 4 divisions, 1 leurre) ───

// Arbitrage prof 2026-09-12 : trois multiplications à trou pour CINQ divisions
// (quatre pour cinq se résolvait par élimination) ; les deux cibles en trop
// sont les inversions de deux couples différents (3 ÷ 5 pour « 5 × … = 3 »,
// 7 ÷ 56 pour « 7 × … = 56 »), la seule famille attestée (DEPP 4e 2023 q.15) :
// l'élève doit trancher deux fois « lequel est en haut ».
const CONSIGNE_RELIER = '<p><b>Relie chaque multiplication à trou à la division qui donne le nombre manquant.</b> Deux divisions sont en trop.</p>';

const genRelier = () => {
    for (let essai = 0; essai < 80; essai++) {
        const couples = [tirerTable(), tirerFraction(), randomChoice([true, false]) ? tirerTable() : tirerFraction()];
        // trois facteurs a distincts et trois résultats distincts : aucune division ne se confond
        if (new Set(couples.map((c) => c.a)).size < 3 || new Set(couples.map((c) => c.b)).size < 3) continue;
        const pairs = shuffle(couples).map((c) => ({ source: multiplicationTrou(c, randomChoice([true, false])), target: `${c.b} ${DIVISE} ${c.a}` }));
        const [c1, c2] = shuffle([...couples]).slice(0, 2);
        const leurres = [c1, c2].map((c) => `${c.a} ${DIVISE} ${c.b}`);
        if (new Set([...pairs.map((p) => p.target), ...leurres]).size !== 5) continue;
        const payload = makeRelier(pairs, CONSIGNE_RELIER, { ...opts(L_SENS), cibles_leurres: leurres.map((target) => ({ target, code: 'inversion' })) });
        payload.contenu = { ...payload.contenu, explications: { inversion: EXPLICATIONS.inversion, regle: EXPLICATIONS.regle } };
        payload._meta.params = { couples, leurres: leurres.map((texte) => ({ texte, code: 'inversion' })) };
        return payload;
    }
    const couples = [verifierCouple(6, 54, true), verifierCouple(3, 7, false), verifierCouple(5, 8, false)];
    const pairs = couples.map((c) => ({ source: multiplicationTrou(c, false), target: `${c.b} ${DIVISE} ${c.a}` }));
    const leurres = [`3 ${DIVISE} 7`, `5 ${DIVISE} 8`];
    const payload = makeRelier(pairs, CONSIGNE_RELIER, { ...opts(L_SENS), cibles_leurres: leurres.map((target) => ({ target, code: 'inversion' })) });
    payload.contenu = { ...payload.contenu, explications: { inversion: EXPLICATIONS.inversion, regle: EXPLICATIONS.regle } };
    payload._meta.params = { couples, leurres: leurres.map((texte) => ({ texte, code: 'inversion' })) };
    return payload;
};

// ─── Contrat distracteurs (référence de revue) ────────────────────────────

export const REGLES_DISTRACTEURS_QCM = {
    invariants_communs: [
        'quatre CALCULS (pas des résultats) : b ÷ a, a ÷ b, b − a ou a − b, a × b ; valeurs deux à deux distinctes, sinon nouveau tirage',
        'les couples sacrifiés (4 ; 13) et 7 × 8 = 56 ne sortent d\'aucune carte',
    ],
    qcm_unique: {
        support: 'a × … = b ou … × a = b, fait de table (56 ÷ 7) une fois sur deux, quotient qui ne tombe pas juste (3 ÷ 5) sinon',
        familles: {
            inversion: 'a ÷ b : le facteur divisé par le résultat (la principale : DEPP 4e 2023 q.15, l\'inversion d\'écriture 4/3 pour 3/4)',
            soustraction: 'b − a (ou a − b) : le trou pris pour ce qui manque, comme dans une addition à trou',
            produit: 'a × b : « il y a un ×, je multiplie » (Eduscol 5e 2025 q.29)',
        },
    },
    relier: 'trois couples, cinq divisions : deux cibles leurres = les inversions a ÷ b de deux couples différents ; facteurs a distincts, résultats b distincts',
};

// ─── Registre et point d'entrée ───────────────────────────────────────────

const GENERATORS = {
    flash_texte_trou_saisie: genFlash,
    calcul_qcm_unique: genQcm,
    sens_relier: genRelier,
};

// etapes : nombre d'étapes de raisonnement de la carte, validé par le prof le
// 2026-10-06 (chantier arbres-4e) ; sert aux exercices complexes du groupe Acquis.
export const CATEGORIES = [
    { id: 'flash', label: L_FLASH, etapes: 1, formatsList: [{ key: 'flash_texte_trou_saisie', label: 'Saisie trous' }] },
    { id: 'calcul', label: L_QCM, etapes: 1, formatsList: [{ key: 'calcul_qcm_unique', label: 'QCM' }] },
    { id: 'sens', label: L_SENS, etapes: 1, formatsList: [{ key: 'sens_relier', label: 'Relier' }] },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter((k) => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
